import { useMemo } from 'react';
import { useField } from 'formik';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Typography, CircularProgress } from '@mui/material';

import { appendInputIdToLabel } from 'utils';
import { QuestionLabel } from 'components/question-label';
import { defaultLanguage, useTranslationContext } from 'context';
import { getTranslatedQuestionLabel } from 'utils/question-label';
import {
  FormField,
  FormFieldType,
  OutcomesFieldValue,
  OutcomeFieldsFieldValue,
} from 'types';

import { DynamicFormFields } from '.';
import { OutcomeFieldsFieldProps } from './types';

const namespaceFields = (fields: FormField[], prefix: string): FormField[] =>
  fields.map((field) => {
    if (field.type === FormFieldType.FIELD_ARRAY) {
      return {
        ...field,
        id: `${prefix}.${field.id}`,
        options: field.options
          ? {
              ...field.options,
              fields: namespaceFields(field.options.fields ?? [], prefix),
            }
          : field.options,
      };
    }

    return {
      ...field,
      id: `${prefix}.${field.id}`,
    };
  });

const getOutcomes = (value: unknown): OutcomesFieldValue => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {
      outcomes: [],
      orderedIds: [],
    };
  }

  const storedValue = value as OutcomeFieldsFieldValue;
  const outcomeIds = Object.keys(storedValue);

  return {
    orderedIds: outcomeIds,
    outcomes: outcomeIds.map((id) => {
      const stored = storedValue[id];
      return {
        id,
        label: typeof stored?.label === 'string' ? stored.label : id,
      };
    }),
  };
};

export const OutcomeFieldsField = ({
  id,
  options,
  sourceValue,
  sourceLoading,
  fieldArrayProps,
}: OutcomeFieldsFieldProps) => {
  const [field] = useField<OutcomeFieldsFieldValue>(id);
  const { t } = useTranslation(['common']);
  const {
    className,
    itemClassName,
    expandableInput,
    hideControlLabels,
    inputLabelOutside,
  } = fieldArrayProps ?? {};
  const { language } = useTranslationContext();
  const storedValue = useMemo(() => getOutcomes(field.value), [field.value]);
  const outcomes = sourceValue?.outcomes.length
    ? sourceValue.outcomes
    : storedValue.outcomes;

  if (sourceLoading && !outcomes.length) {
    return <CircularProgress size={24} />;
  }

  if (!outcomes.length) {
    return (
      <Typography variant="body2" className="text-gray-neutral">
        {t('noOutcomesAvailable')}
      </Typography>
    );
  }

  // No sub-fields configured — this is a form misconfiguration; render nothing.
  if (!options?.fields?.length) {
    return null;
  }

  return (
    <Box className="flex flex-col gap-4">
      {outcomes.map((outcome, index) => {
        const namespacedFields = namespaceFields(
          options.fields,
          `${id}.${outcome.id}`
        );

        return (
          <Box key={outcome.id}>
            <Typography className="mb-3 font-semibold">
              {outcome.label}
            </Typography>

            <Box
              className={twMerge(
                'flex flex-col',
                hideControlLabels && 'grid gap-y-2 md:grid-cols-2 md:gap-x-4',
                className
              )}
            >
              <DynamicFormFields
                fields={namespacedFields}
                expandableInput={expandableInput}
                fieldArrayProps={fieldArrayProps}
                inputLabelOutside={inputLabelOutside}
                hideLabels={inputLabelOutside !== false}
                container={(field, node, fieldIndex) => {
                  let label = getTranslatedQuestionLabel(
                    field,
                    language,
                    defaultLanguage
                  );
                  label = appendInputIdToLabel(label, field.inputId);

                  return (
                    <Box
                      key={field.id}
                      className={twMerge(
                        'flex w-full flex-1 gap-2',
                        'flex-col',
                        fieldIndex > 0 && 'mt-2',
                        itemClassName
                      )}
                    >
                      {!hideControlLabels && (
                        <Box className="control w-full">
                          <Typography component="div" className="mb-1 w-full">
                            <QuestionLabel value={label} />
                          </Typography>
                          {node}
                        </Box>
                      )}
                      {hideControlLabels && (
                        <Box className="control w-full [&>div]:w-full">
                          {node}
                        </Box>
                      )}
                    </Box>
                  );
                }}
              />
            </Box>

            {index < outcomes.length - 1 && <Divider className="mt-4" />}
          </Box>
        );
      })}
    </Box>
  );
};
